// StudioEditor — the Studio in three columns (pen "Studio", Decided 29 Oct;
// #315): the top bar across, then the outline on the left (Outline.tsx),
// the material one page at a time in the middle (ContainerPage.tsx,
// DocumentPage.tsx; Info and Sources, AboutPage.tsx) and the action panel
// (Chat | Needs you, with the composer at the bottom; rendered by the
// host).
//
// What is shown is the desk's path (studioDesk.ts), projected into the root,
// the outline and the page (studioPages.ts). Every page is a document one
// writes on (pen "Always writing"): a course or module page too, its parts
// among its text (renderContainer). Going in: a row, a page link.
// Going out: a breadcrumb, Back, or Esc one level up. Pure: the cards and
// the place are projected in; every change goes out as an intent. Local
// state is view-ephemeral only (the author's folds in the outline).

export { StudioEditor };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type { PartIntent } from "../ModuleCard/partOps.js";
import type {
    ContainerCard,
    ContainerPart,
    StudioCard,
} from "../Studio/studioCards.js";
import type {
    DeskAbout,
    DeskCommand,
    DeskDocument,
    DeskState,
} from "../Studio/studioDesk.js";
import type { DeskBriefs } from "../StudioDesk/deskFacts.js";
import { deskKeyDown } from "../StudioDesk/deskKeys.js";
import { AboutPage } from "./AboutPage.js";
import { ContainerPage } from "./ContainerPage.js";
import { type CaretAt, caretOf } from "./caretOf.js";
import { DocumentPage } from "./DocumentPage.js";
import type { LineAsk, LineIntents } from "./Lines.js";
import { Outline } from "./Outline.js";
import { landingChoices } from "./studioCreate.js";
import {
    aboutRows,
    escapeCommands,
    outlineRows,
    type PageLink,
    studioPage,
    type Waiting,
} from "./studioPages.js";
import "../StudioDesk/StudioDesk.css";
import "./StudioEditor.css";

type StudioEditorProps = {
    // What lies on the project's canvas: everything the Studio shows.
    readonly cards: readonly StudioCard[];
    readonly desk: DeskState;
    readonly projectTitle: string;
    readonly online: boolean;
    // What waits for the author, by resource id.
    readonly waiting: Waiting;
    // The bar across the top, and the action panel.
    readonly topBar: ReactNode;
    readonly right: ReactNode;
    // Help and feedback, at the outline's foot (absent: not here).
    readonly help?: ReactNode;
    // The project's sources (undefined: not known yet), for the page's
    // facts line; its count opens the Sources page.
    readonly sources: number | undefined;
    readonly onSources: () => void;
    // A change of the page shown.
    readonly onDesk: (...commands: readonly DeskCommand[]) => void;
    // A part of a container moved or taken out.
    readonly onPart: (container: ContainerCard, intent: PartIntent) => void;
    // A part of a container into another container (at its end).
    readonly onMoveTo: (from: ContainerCard, part: string, to: string) => void;
    // A module's next one in its course.
    readonly onDuplicate: (courseId: string, module: ContainerPart) => void;
    // The middle of the document shown (the editor, a source's original).
    readonly renderDocument: (doc: DeskDocument) => ReactNode;
    // A course or module page's text with its parts among it (`part` draws
    // one by id), or null while it is not ready (the parts show as a list).
    readonly renderContainer: (
        id: string,
        part: (id: string) => ReactNode,
    ) => ReactNode | null;
    // What a page about the project holds (Info, Sources).
    readonly renderAbout: (about: DeskAbout) => ReactNode;
    // A learning objective has no part yet (the outline row's dot).
    readonly uncovered: boolean;
    // The Create menu with its button: at the outline's foot, or the first
    // line of an empty page.
    readonly renderCreate: (at: "outline" | "page") => ReactNode;
    // The + in a line's margin: the Create menu, landing under that part.
    readonly renderAdd: (link: PageLink) => ReactNode;
    readonly briefs: DeskBriefs;
    // A course, module or document page's new name (`previous` comes back
    // if saving fails).
    readonly onRename: (id: string, title: string, previous: string) => void;
    // A line that asks, on the page of `container` (absent: the project's
    // own page), where the part would land.
    readonly ask?: LineAsk & { readonly container: string | undefined };
    // The caret moved to a block of the page (with what is selected there),
    // so the assistant knows where the author is. View state.
    readonly onCaret?: (at: CaretAt) => void;
};

const StudioEditor = ({
    cards,
    desk,
    projectTitle,
    online,
    waiting,
    topBar,
    right,
    help,
    sources,
    onSources,
    onDesk,
    onPart,
    onMoveTo,
    onDuplicate,
    renderDocument,
    renderContainer,
    renderAbout,
    uncovered,
    renderCreate,
    renderAdd,
    briefs,
    onRename,
    ask,
    onCaret,
}: StudioEditorProps): ReactNode => {
    const { t } = useLingui();
    // The author's folds in the outline (by row key).
    const [folds, setFolds] = useState<ReadonlyMap<string, boolean>>(new Map());
    const page = studioPage(cards, desk, projectTitle, waiting);
    const open = (commands: readonly DeskCommand[]) => onDesk(...commands);
    const goUp = () => open(escapeCommands(cards, desk));
    const reportCaret = (target: EventTarget | null) => {
        const at = caretOf(target);
        if (at !== undefined) onCaret?.(at);
    };
    const links: LineIntents = {
        onOpen: open,
        onMove: (link: PageLink, to: number) =>
            link.holder === undefined
                ? undefined
                : onPart(link.holder.container, {
                      type: "move",
                      part: link.id,
                      from: link.holder.at,
                      to,
                  }),
        onMoveTo: (link: PageLink, to: string) =>
            link.holder === undefined
                ? undefined
                : onMoveTo(link.holder.container, link.id, to),
        onRename: (link: PageLink, title: string) =>
            onRename(link.id, title, link.title),
        renderAdd,
        onTrash: (link: PageLink) =>
            link.holder === undefined
                ? undefined
                : onPart(link.holder.container, {
                      type: "remove",
                      part: link.id,
                      at: link.holder.at,
                  }),
        onDuplicate: (link: PageLink) =>
            link.holder === undefined
                ? undefined
                : onDuplicate(link.holder.container.id, link.holder.part),
    };
    // A container page's first line when empty, and where parts can move.
    const containerParts = {
        sources,
        onSources,
        add: renderCreate("page"),
        places: landingChoices(cards),
    };
    // The asking line, on its container's page only.
    const askOn = (container: string | undefined) =>
        ask !== undefined && ask.container === container ? ask : undefined;

    const middle = ((): ReactNode => {
        switch (page.type) {
            case "root": {
                // The project's one course, if it is one.
                const course = page.container;
                return (
                    <ContainerPage
                        key="root"
                        title={page.title}
                        container={course}
                        links={page.links}
                        online={online}
                        note={
                            course === undefined
                                ? undefined
                                : briefs.notes.get(course.id)
                        }
                        {...containerParts}
                        onRename={undefined}
                        intents={links}
                        ask={askOn(course?.id)}
                        body={
                            course === undefined
                                ? undefined
                                : (part) => renderContainer(course.id, part)
                        }
                    />
                );
            }
            case "container": {
                const container = page.container;
                return (
                    <ContainerPage
                        key={page.id}
                        title={page.title}
                        container={page.container}
                        links={page.links}
                        online={online}
                        note={briefs.notes.get(page.id)}
                        {...containerParts}
                        onRename={
                            online && container !== undefined
                                ? (title) =>
                                      onRename(container.id, title, page.title)
                                : undefined
                        }
                        intents={links}
                        ask={askOn(page.id)}
                        body={(part) => renderContainer(page.id, part)}
                    />
                );
            }
            case "document":
                return (
                    <DocumentPage
                        key={page.id}
                        doc={page.doc}
                        kind={page.kind}
                        suggested={page.suggested}
                        sources={sources}
                        onSources={onSources}
                        back={page.back}
                        next={page.next}
                        middle={renderDocument(page.doc)}
                        note={briefs.notes.get(page.id)}
                        onRename={
                            online
                                ? (title) =>
                                      onRename(page.id, title, page.doc.title)
                                : undefined
                        }
                        onOpen={open}
                    />
                );
            case "about":
                return (
                    <AboutPage
                        key={page.id}
                        about={page.id}
                        title={projectTitle}
                    >
                        {renderAbout(page.id)}
                    </AboutPage>
                );
            default:
                return exhaustSwitchCase(page);
        }
    })();

    return (
        <section className="bilda-pages" aria-label={t`Studio`}>
            {topBar}
            <div className="bilda-pages__columns">
                <Outline
                    about={aboutRows(desk, uncovered)}
                    rows={outlineRows(
                        cards,
                        desk,
                        projectTitle,
                        folds,
                        waiting,
                    )}
                    onOpen={open}
                    onFold={(key, unfolded) =>
                        setFolds((m) => new Map(m).set(key, unfolded))
                    }
                    foot={renderCreate("outline")}
                    help={help}
                />
                <section
                    className="bilda-pages__paper"
                    aria-label={t`Material`}
                    onKeyDown={deskKeyDown(goUp)}
                    onFocus={(e) => reportCaret(e.target)}
                    onSelect={(e) => reportCaret(e.target)}
                >
                    {middle}
                </section>
                {right}
            </div>
        </section>
    );
};
